import { useAuth } from "@repo/auth";
import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import type { GuaranteeResponse } from "@repo/api-client";
import { PageScaffold } from "../../shell/PageScaffold";
import { useVenue } from "../../shell/VenueContext";
import { canAccessVenue } from "../../shell/roles";
import { formatFee } from "./noShowChargeApi";
import { useFailedGuarantees } from "./queries";

const cell = css({ padding: "2", textAlign: "left", verticalAlign: "top", whiteSpace: "nowrap" });
const table = css({ width: "full", borderCollapse: "collapse", fontSize: "sm" });
const scroll = css({ overflowX: "auto", width: "full" });

/**
 * `/v/:venueId/guarantees/failed` — the "Failed no-show charges" surface (card-fingerprint-no-show.md
 * R10). A charge whose credential was declined/expired/cancelled is recorded FAILED with NO auto-retry
 * and surfaced here for manager follow-up. MANAGER-gated (the server re-enforces); reads
 * `GET /guarantees/failed`. Each row shows the booking, the card ending/brand, the snapshot fee and the
 * provider decline message.
 */
export function FailedChargesPage() {
  const { venueId } = useVenue();
  const { claims } = useAuth();
  const { t, i18n } = useTranslation();
  const canView = canAccessVenue(claims, venueId, "MANAGER");
  const q = useFailedGuarantees(venueId, canView);

  if (!canView) {
    return (
      <PageScaffold title={t("app.noShowCharge.failedList.title")}>
        <p>{t("deposits.forbidden")}</p>
      </PageScaffold>
    );
  }

  const rows = q.data ?? [];

  return (
    <PageScaffold title={t("app.noShowCharge.failedList.title")}>
      {q.isLoading ? (
        <p>{t("deposits.loading")}</p>
      ) : q.isError ? (
        <p role="alert" className={css({ color: "fg.error" })} data-testid="failed-error">
          {t("app.noShowCharge.failedList.loadError")}
        </p>
      ) : rows.length === 0 ? (
        <p data-testid="failed-empty">{t("app.noShowCharge.failedList.empty")}</p>
      ) : (
        <div className={scroll}>
          <table className={table} data-testid="failed-table">
            <thead>
              <tr>
                <th className={cell}>{t("app.noShowCharge.failedList.col.booking")}</th>
                <th className={cell}>{t("app.noShowCharge.failedList.col.card")}</th>
                <th className={cell}>{t("app.noShowCharge.failedList.col.fee")}</th>
                <th className={cell}>{t("app.noShowCharge.failedList.col.reason")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <FailedRow key={row.bookingId ?? String(i)} row={row} locale={i18n.language} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </PageScaffold>
  );
}

function FailedRow({ row, locale }: { row: GuaranteeResponse; locale: string }) {
  const { t } = useTranslation();
  const fee =
    row.feeMinor !== undefined && row.currency
      ? formatFee(row.feeMinor, row.currency, locale)
      : "—";
  const card = row.cardLast4
    ? t("app.noShowCharge.card", { last4: row.cardLast4, brand: row.cardBrand ?? "" })
    : "—";
  // Booking reference: the short, copyable handle a manager uses to find the booking. (Guest name +
  // service date would be clearer — that needs the failed-guarantees endpoint to include them.)
  const ref = row.bookingId ? row.bookingId.slice(0, 8) : "—";
  return (
    <tr data-testid={`failed-row-${row.bookingId ?? ""}`}>
      <td
        className={css({ padding: "2", verticalAlign: "top", fontFamily: "mono", fontSize: "xs" })}
      >
        {t("app.noShowCharge.failedList.ref", { ref })}
      </td>
      <td className={cell}>{card}</td>
      <td className={cell}>{fee}</td>
      <td className={css({ padding: "2", verticalAlign: "top", color: "fg.muted" })}>
        {row.providerMessage ?? "—"}
      </td>
    </tr>
  );
}
